/**
 * The realtime client.
 *
 * Events are **synchronisation hints, not data**. When one arrives the client
 * marks the affected queries stale and lets the normal fetch path re-read the
 * authoritative record; it never writes the event payload into a cache as
 * truth. That rule is what keeps a dropped socket from producing a screen that
 * quietly disagrees with the database — the worst failure mode a realtime
 * feature has, because nothing looks broken.
 *
 * The socket carries no authority of its own either. Channels are granted by
 * the server from the token, never requested by the client, so subscribing to
 * `role:payroll_administrator` is not something a curious employee can do by
 * sending a frame.
 */

export interface RealtimeEvent {
  id: string;
  name: string;
  payload: Record<string, unknown>;
}

export type RealtimeStatus =
  'connected' | 'connecting' | 'reconnecting' | 'offline' | 'authentication_required' | 'disabled';

export interface RealtimeOptions {
  url: string;
  /** Read fresh each connect: a reconnect after a refresh needs the new token. */
  getAccessToken: () => string | null | Promise<string | null>;
  onEvent: (event: RealtimeEvent) => void;
  onStatusChange?: (status: RealtimeStatus) => void;
  /** Injected so tests do not need a real WebSocket. */
  socketFactory?: (url: string) => WebSocketLike;
  enabled?: boolean;
}

/** The slice of WebSocket this client uses, so a fake is small to write. */
export interface WebSocketLike {
  send(data: string): void;
  close(code?: number, reason?: string): void;
  onopen: ((event: unknown) => void) | null;
  onclose: ((event: { code?: number }) => void) | null;
  onerror: ((event: unknown) => void) | null;
  onmessage: ((event: { data: unknown }) => void) | null;
}

/** How long to wait before each reconnection attempt. */
const BACKOFF_MS = [1_000, 2_000, 5_000, 10_000, 30_000];

/**
 * How many event ids to remember for deduplication.
 *
 * Delivery is at-least-once, and a reconnect replays. Without this the same
 * approval produces two toasts, which reads as two approvals.
 */
const SEEN_LIMIT = 500;

export class RealtimeClient {
  private socket: WebSocketLike | null = null;
  private status: RealtimeStatus = 'offline';
  private attempt = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private closedByUs = false;
  private readonly seen = new Set<string>();
  private readonly seenOrder: string[] = [];

  constructor(private readonly options: RealtimeOptions) {}

  getStatus(): RealtimeStatus {
    return this.status;
  }

  async connect(): Promise<void> {
    if (this.options.enabled === false) {
      this.setStatus('disabled');
      return;
    }
    if (this.socket) return;

    this.closedByUs = false;
    this.setStatus(this.attempt === 0 ? 'connecting' : 'reconnecting');

    const token = await this.options.getAccessToken();
    if (!token) {
      this.setStatus('authentication_required');
      return;
    }

    const url = `${this.options.url}?token=${encodeURIComponent(token)}`;
    const socket = (this.options.socketFactory ?? defaultSocketFactory)(url);
    this.socket = socket;

    socket.onopen = () => {
      this.attempt = 0;
      this.setStatus('connected');
    };

    socket.onmessage = (event) => this.handleMessage(event.data);

    socket.onerror = () => {
      /* A close always follows; reconnecting from both would double the rate. */
    };

    socket.onclose = (event) => {
      this.socket = null;

      // 4401 is the server saying the session is gone. Reconnecting would fail
      // identically and forever, so it stops and says why.
      if (event?.code === 4401) {
        this.setStatus('authentication_required');
        return;
      }

      if (this.closedByUs) {
        this.setStatus('offline');
        return;
      }

      this.scheduleReconnect();
    };
  }

  disconnect(): void {
    this.closedByUs = true;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.socket?.close();
    this.socket = null;
    this.setStatus('offline');
  }

  /** Reconnect with a fresh token — after a refresh, or on returning to foreground. */
  async reconnect(): Promise<void> {
    this.disconnect();
    this.attempt = 0;
    await this.connect();
  }

  private handleMessage(raw: unknown): void {
    let frame: { type?: string; id?: string; name?: string; payload?: Record<string, unknown> };
    try {
      frame = JSON.parse(String(raw));
    } catch {
      return;
    }

    if (frame.type === 'pong' || frame.type === 'connected') return;
    if (frame.type !== 'event' || !frame.id || !frame.name) return;

    // At-least-once delivery plus replay on reconnect means the same event
    // arrives more than once. Showing it twice reads as it happening twice.
    if (this.seen.has(frame.id)) return;
    this.remember(frame.id);

    this.options.onEvent({ id: frame.id, name: frame.name, payload: frame.payload ?? {} });
  }

  private remember(id: string): void {
    this.seen.add(id);
    this.seenOrder.push(id);
    if (this.seenOrder.length > SEEN_LIMIT) {
      const oldest = this.seenOrder.shift();
      if (oldest) this.seen.delete(oldest);
    }
  }

  private scheduleReconnect(): void {
    const delay = BACKOFF_MS[Math.min(this.attempt, BACKOFF_MS.length - 1)]!;
    this.attempt += 1;

    // Only the first drop is worth telling anybody about; a brief blip that
    // recovers in a second should not put a banner on the screen.
    this.setStatus('reconnecting');

    this.timer = setTimeout(() => {
      void this.connect();
    }, delay);
    (this.timer as { unref?: () => void }).unref?.();
  }

  private setStatus(next: RealtimeStatus): void {
    if (this.status === next) return;
    this.status = next;
    this.options.onStatusChange?.(next);
  }
}

function defaultSocketFactory(url: string): WebSocketLike {
  const Ctor = (globalThis as { WebSocket?: new (url: string) => WebSocketLike }).WebSocket;
  if (!Ctor) throw new Error('No WebSocket implementation is available in this environment.');
  return new Ctor(url);
}
